import { useState, useCallback, useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { FuelCharge } from '../types/fuel-charge-types';

export const useFuelChargeManagement = () => {
  const [fuelCharges, setFuelCharges] = useState<FuelCharge[]>([]);

  const hasAnyFuelCharge = useMemo(() => fuelCharges.some((fc) => fc.amount > 0), [fuelCharges]);

  const addFuelCharge = useCallback((orderProductId: number, unitId: number, amount: number, productId?: number) => {
    setFuelCharges((prev) => {
      const existing = prev.find((fc) => fc.orderProductId === orderProductId && fc.unitId === unitId);

      if (existing) {
        return prev.map((fc) =>
          fc.uuid === existing.uuid ? { ...fc, amount, productId: productId || fc.productId } : fc,
        );
      }

      return [
        ...prev,
        {
          orderProductId,
          productId,
          unitId,
          amount,
          uuid: uuidv4() + 'fuel',
        },
      ];
    });
  }, []);

  const removeFuelChargesForProduct = useCallback((orderProductId: number) => {
    setFuelCharges((prev) => prev.filter((fc) => fc.orderProductId !== orderProductId));
  }, []);

  const getFuelChargeForUnit = useCallback(
    (orderProductId: number, unitId: number) => {
      return fuelCharges.find((fc) => fc.orderProductId === orderProductId && fc.unitId === unitId);
    },
    [fuelCharges],
  );

  const updateFuelCharge = useCallback(
    (orderProductId: number, unitId: number, updates: { amount: number; productId?: number }) => {
      setFuelCharges((prev) => {
        const existing = prev.find((fc) => fc.orderProductId === orderProductId && fc.unitId === unitId);

        if (existing) {
          // Update existing fuel charge
          return prev.map((fc) =>
            fc.uuid === existing.uuid
              ? { ...fc, amount: updates.amount, productId: updates.productId || fc.productId }
              : fc,
          );
        } else {
          // Create new fuel charge if it doesn't exist
          return [
            ...prev,
            {
              orderProductId,
              unitId,
              amount: updates.amount,
              productId: updates.productId,
              uuid: uuidv4() + 'fuel',
            },
          ];
        }
      });
    },
    [],
  );

  return {
    // State
    fuelCharges,
    hasAnyFuelCharge,

    // Actions
    setFuelCharges,
    addFuelCharge,
    removeFuelChargesForProduct,

    // Helper functions
    getFuelChargeForUnit,
    updateFuelCharge,
  };
};
